import { Provider as BalancerProvider } from "react-wrap-balancer";
import { builder } from "@builder.io/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SpeedInsights } from "@vercel/speed-insights/next";
import type { AppProps } from "next/app";
import Head from "next/head";

import { Favicon } from "@/components/Favicon";
import { SEO } from "@/components/SEO";
import { ApolloProvider, client } from "@/lib/apollo-client";
import { queryClient } from "@/lib/react-query-client";
import { ServicesContainer } from "@/services";
import { AppWrapper } from "@/services/store";

import "../styles/fonts.css";
import "../styles/globals.css";

builder.init(process.env.NEXT_PUBLIC_BUILDER_IO_KEY as string);

import type { ReactElement, ReactNode } from "react";
import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import type { NextPage } from "next";
import localFont from "next/font/local";
import { usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";

import { analytics } from "@flare/analytics";
import { I18nextProvider } from "@flare/i18n";

import { useSetAnalyticsAnonymousId } from "@/hooks/useSetAnalyticsAnonymousId";
import { AuthProvider } from "@/modules/auth/auth-provider";
import { SplitProvider } from "@/modules/Split";
import { BiEventsNames } from "@/services/analytics/bi-event-names";
import { initializeAnalytics } from "@/services/analytics/initialize-analytics";
import { useFeatureFlag } from "@/services/experiments";
import { featureFlagNames } from "@/services/experiments/constants";
import { i18n } from "@/services/locale/i18n";
import { ExperimentProvider } from "@/services/statsig/experiment-provider";
import { useUserSession } from "@/services/store/user-session/user-session";
import { getAnonymousId } from "@/utils/anonymous-id";
import { getE2EExperimentOverrides } from "@/utils/e2e/getE2EExperimentOverrides";
import { isE2ETest } from "@/utils/e2e/isE2ETest";
import { isUnitTest } from "@/utils/is-unit-tests";
import { trackWebVitals } from "@/utils/web-vitals";

import "@/builder/common-registry";
import "@builder.io/widgets";

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const serif = localFont({
  src: [
    {
      path: "../../public/fonts/BogueLight.woff2",
      weight: "300",
    },
    {
      path: "../../public/fonts/BogueRegular.woff2",
      weight: "400",
    },
  ],
  variable: "--font-family-serif",
});

const sans = localFont({
  src: [
    {
      path: "../../public/fonts/RoobertBold.woff2",
      weight: "600",
    },
    {
      path: "../../public/fonts/RoobertMedium.woff2",
      weight: "500",
    },
    {
      path: "../../public/fonts/RoobertRegular.woff2",
      weight: "400",
    },
  ],
  variable: "--font-family-sans",
});

initializeAnalytics();

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  useUserSession();
  const pathname = usePathname();

  useSetAnalyticsAnonymousId();

  /**
   * https://nextjs.org/docs/pages/api-reference/functions/use-report-web-vitals
   */
  useReportWebVitals((metric) => {
    if (typeof window === "undefined" || !pathname) return;

    if (pathname.includes("/case-urgency")) {
      // save each type of metric once per case urgency page
      trackWebVitals("caseUrgencyWebVitals", metric);
    } else if (pathname.startsWith("/v2/funnel")) {
      // save each type of metric once per start funnel
      trackWebVitals("startFunnelWebVitals", metric);
    } else {
      // save each type of metric once per user session
      trackWebVitals("startSessionWebVitals", metric);
    }
  });

  useFeatureFlag(featureFlagNames.FUNNEL_TEST_PRE_CLIENT_PORTAL);

  /**
   * Fire page load cross event as soon as possible,
   * this is temporary until we have a better solution
   */
  useEffect(() => {
    analytics.track(BiEventsNames.WebPageLoadCross);
  }, []);

  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <>
      {/* TODO: Remove this when we migrate to app router */}
      {/* Sets the main font on the <body> tag and resolves the React portal issue */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx global>{`
        body {
          font-family: ${sans.style.fontFamily};
        }
      `}</style>
      <I18nextProvider i18n={i18n}>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
          <Favicon />
        </Head>
        <SEO {...(pageProps?.story?.seo || {})} />
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <ApolloProvider client={client}>
              <AppWrapper>
                <BalancerProvider>
                  {/* Currently disabled due to product requirements */}
                  {/* <GoogleOneTapSignIn parentContainerId="google-one-tap" /> */}
                  <div
                    className={`${serif.variable} ${sans.variable} font-sans`}
                    id="appContainer"
                  >
                    <SplitProvider>
                      <ExperimentProvider
                        apiKey={
                          process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY as string
                        }
                        disabled={isE2ETest() || isUnitTest()}
                        initData={
                          pageProps?.statsigProps || {
                            user: {
                              userID: getAnonymousId(),
                              custom: {
                                environment:
                                  process.env.NODE_ENV === "production"
                                    ? "production"
                                    : "development",
                              },
                            },
                          }
                        }
                        experimentOverrides={getE2EExperimentOverrides()}
                      >
                        {getLayout(<Component {...pageProps} />)}
                        <ReactQueryDevtools />
                      </ExperimentProvider>
                    </SplitProvider>
                  </div>
                  <ServicesContainer pageProps={pageProps} />
                </BalancerProvider>
              </AppWrapper>
            </ApolloProvider>
          </QueryClientProvider>
        </AuthProvider>
        <SpeedInsights />
      </I18nextProvider>
    </>
  );
}

export default MyApp;
